<template>
  <LandingBackground>
    <div class="mb-20">
      <div class="mx-auto mt-10 grid grid-cols-1 items-start gap-10 sm:px-5 md:px-8 xl:grid-cols-2 3xl:grid-cols-3">
        <div class="flex min-w-0 flex-col gap-10">
          <LandingCard variant="info" :title="`Welcome, ${userRef.firstName} ${userRef.lastName}`">
            <template #default>
              <LandingCardSkeleton v-if="isGetDistributorsPending" class="mt-6" />

              <LandingCardLinkSections label="Getting Started" v-if="hasNoDistributors">
                <LandingCardLinkSection
                  link-label="Request Access to a Distributor"
                  description="If you need to submit distributor reports and product list updates, request access to a distributor to get started. You will be notified after your request is reviewed and access is granted."
                  :to="{ name: routeNames.accessRequest.distributor }"
                />
              </LandingCardLinkSections>

              <LandingCardLinkSections label="Monthly Distributor Reports" v-if="hasMonthlyDistributors">
                <p class="text-sm leading-normal text-neutral-600">
                  For beverage distributors who annually import or distribute more than 100,000 deposit beverage
                  containers.
                </p>
                <PublicLandingList>
                  <PublicLandingListItem label="Reports will be scheduled on the 1st of the month" />
                  <PublicLandingListItem label="Submit reports on or before the 15th of the month" />
                </PublicLandingList>
              </LandingCardLinkSections>

              <LandingCardLinkSections label="Semi-Annual Distributor Reports" v-if="hasSemiAnnualDistributors">
                <p class="text-sm leading-normal text-neutral-600">
                  For beverage distributors who annually import or distribute 100,000 or fewer deposit beverage
                  containers.
                </p>
                <PublicLandingList>
                  <PublicLandingListItem label="Reports will be scheduled on January 1st and July 1st" />
                  <PublicLandingListItem label="Submit reports on or before January 15th and July 15th" />
                </PublicLandingList>
              </LandingCardLinkSections>

              <LandingCardLinkSections
                label="Distributor Registration Updates"
                v-if="hasMonthlyDistributors || hasSemiAnnualDistributors"
              >
                <LandingCardLinkSection
                  link-label="Submit a Distributor Change Notification"
                  description="Please notify the Deposit Beverage Container Program if there are any changes to the following:"
                  :to="{ name: routeNames.changeNotification.distributorNew }"
                />
                <PublicLandingList>
                  <PublicLandingListItem label="Company ownership" />
                  <PublicLandingListItem label="Business name" />
                  <PublicLandingListItem label="Mailing address" />
                  <PublicLandingListItem label="Operational status" />
                </PublicLandingList>
              </LandingCardLinkSections>

              <LandingCardLinkSections label="Questions">
                <p class="text-sm leading-normal text-neutral-600">
                  Contact the Deposit Beverage Container Program at
                  <a class="text-sky-600 hover:underline" href="mailto:DOH.DBCDistributor@doh.hawaii.gov"
                    >DOH.DBCDistributor@doh.hawaii.gov</a
                  >
                  or <a class="text-sky-600 hover:underline" href="tel:+18085864226">(808) 586-4226</a> if you have any
                  questions.
                </p>
              </LandingCardLinkSections>
            </template>
          </LandingCard>
        </div>

        <div class="flex min-w-0 flex-col gap-10 xl:col-start-2 3xl:contents">
          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-2">
            <LandingCard title="Distributor Reports" :view-all-to="{ name: routeNames.distributorReport.list }">
              <div
                v-if="
                  isGetDistributorReportsSuccess &&
                  !pastDueReports?.length &&
                  !draftReports?.length &&
                  !scheduledReports?.length
                "
                class="bg-neutral-50 p-10 sm:rounded-b-lg"
              >
                <NoneFoundFlag label="No Distributor Reports" />
              </div>
              <LandingCardSkeleton v-if="isGetDistributorReportsPending" />
              <LandingCardEntityList title="Past Due" variant="warn" :items="pastDueReports">
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      <LookupDescription :murn="item.frequencyMurn" />
                      Distributor Report
                    </template>
                    <template #secondaryLabel>
                      {{ item.distributor?.name ?? item.distributorName }}
                    </template>
                    <template #additionalInfo>
                      <div class="mt-1.5 text-sm md:mt-0 md:self-center md:pr-4 md:text-right">
                        <span class="text-neutral-500">Due</span> {{ formatDueDate(item) }}
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
              <LandingCardEntityList title="Draft" :items="draftReports">
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      <LookupDescription :murn="item.frequencyMurn" />
                      Distributor Report
                    </template>
                    <template #secondaryLabel>
                      {{ item.distributor?.name ?? item.distributorName }}
                    </template>
                    <template #additionalInfo>
                      <div class="mt-1.5 text-sm md:mt-0 md:self-center md:pr-4 md:text-right">
                        <span class="text-neutral-500">Due</span> {{ formatDueDate(item) }}
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
              <LandingCardEntityList title="Scheduled" :items="scheduledReports">
                <template #default="{ item }">
                  <LandingCardEntityListItem>
                    <template #primaryLabel>
                      <LookupDescription :murn="item.frequencyMurn" />
                      Distributor Report
                    </template>
                    <template #secondaryLabel>
                      {{ item.distributor?.name ?? item.distributorName }}
                    </template>
                    <template #additionalInfo>
                      <div class="mt-1.5 text-sm md:mt-0 md:self-center md:pr-4 md:text-right">
                        <span class="text-neutral-500">Due</span> {{ formatDueDate(item) }}
                      </div>
                    </template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
            </LandingCard>
          </div>

          <div class="flex min-w-0 flex-col gap-10 3xl:col-start-3">
            <LandingCard title="My Distributors" :view-all-to="myDistributorsViewAllTo">
              <LandingCardSkeleton v-if="isGetDistributorsPending" />
              <LandingCardEntityList :items="distributors?._items">
                <template #default="{ item }">
                  <LandingCardEntityListItem :primary-label="item.name">
                    <template #secondaryLabel><LookupDescription :murn="item.reportFrequencyMurn" /></template>
                  </LandingCardEntityListItem>
                </template>
              </LandingCardEntityList>
              <div class="border-t border-neutral-200 px-4 py-5 sm:px-6">
                <LandingCardLinkSection
                  link-label="Request Access to a Distributor"
                  description="If you need to submit distributor reports and product list updates, request access to distributor to get started."
                  :to="{ name: routeNames.accessRequest.distributor }"
                />
              </div>
            </LandingCard>
          </div>
        </div>
      </div>
    </div>
  </LandingBackground>
</template>

<script lang="ts">
import type { LandingCardEntity } from "@/components/landing/landingCard";
import LandingCard from "@/components/landing/LandingCard.vue";
import LandingCardEntityList from "@/components/landing/LandingCardEntityList.vue";
import LandingCardEntityListItem from "@/components/landing/LandingCardEntityListItem.vue";
import LandingCardLinkSection from "@/components/landing/LandingCardLinkSection.vue";
import LandingCardLinkSections from "@/components/landing/LandingCardLinkSections.vue";
import LandingCardSkeleton from "@/components/landing/LandingCardSkeleton.vue";
import LandingBackground from "@/components/LandingBackground.vue";
import { LE } from "@/constants/enums";
import { routeNames } from "@/routes";
import { getDistributorReports, getDistributors } from "@/services/endpoints";
import type { Distributor } from "@/views/distributor/distributor";
import distributorService from "@/views/distributor/distributorService";
import type { DistributorReport } from "@/views/distributorReport/distributorReport";
import distributorReportService from "@/views/distributorReport/distributorReportService";
import { NoneFoundFlag, useAppSettingsStore } from "@modernary/mui/components/common";
import { adaptPagedResponse, LookupDescription } from "@modernary/mui/components/lookup";
import { PublicLandingList, PublicLandingListItem } from "@modernary/mui/components/publicLanding";
import type { Paging } from "@modernary/mui/utils/api";
import { useApi } from "@modernary/mui/utils/api";
import { useUser } from "@modernary/mui/utils/authentication";
import { sysDate } from "@modernary/mui/utils/filters";
import { computed, defineComponent, watch } from "vue";
import type { RouteLocationRaw } from "vue-router";

export default defineComponent({
  name: "HomeExternal",
  components: {
    LandingBackground,
    LandingCard,
    LandingCardLinkSection,
    LandingCardLinkSections,
    LandingCardEntityList,
    LandingCardEntityListItem,
    LandingCardSkeleton,
    LookupDescription,
    NoneFoundFlag,
    PublicLandingList,
    PublicLandingListItem,
  },
  setup() {
    const { userRef } = useUser();
    const appSettingsStore = useAppSettingsStore();

    const isAuthenticated = computed(() => userRef.value.isAuthenticated);

    const {
      dataRef: distributorReports,
      exec: execGetDistributorReports,
      isStatusPending: isGetDistributorReportsPending,
      isStatusSuccess: isGetDistributorReportsSuccess,
    } = useApi(getDistributorReports, {
      responseAdapter: async (response) => {
        const data = await adaptPagedResponse(response, distributorReportService.skinnyResponseAdapter);
        data._items.forEach((item: any) => {
          item.to = getDistributorReportRoute(item);
        });
        return data as Paging<LandingCardEntity<DistributorReport>>;
      },
    });

    const {
      dataRef: distributors,
      exec: execGetDistributors,
      isStatusPending: isGetDistributorsPending,
      isStatusSuccess: isGetDistributorsSuccess,
    } = useApi(getDistributors, {
      responseAdapter: async (response) => {
        const data = await adaptPagedResponse(response, distributorService.skinnyResponseAdapter);
        data._items.forEach((item: any) => {
          item.to = { name: routeNames.distributor.view, params: { id: item.id } };
        });
        return data as Paging<LandingCardEntity<Distributor>>;
      },
    });

    watch(
      isAuthenticated,
      (newValue) => {
        if (newValue) {
          execGetDistributorReports({
            statusMurnIn: [LE.ReportStatus.Draft, LE.ReportStatus.Scheduled, LE.ReportStatus.PastDue],
            orderBy: [{ propertyName: "dueDate", isDescending: false }],
            take: 100,
          });
          execGetDistributors({
            orderBy: [{ propertyName: "name", isDescending: false }],
            take: 100,
          });
        }
      },
      { immediate: true },
    );

    const pastDueReports = computed(() =>
      distributorReports.value?._items.filter((item) => item.statusMurn === LE.ReportStatus.PastDue),
    );

    const draftReports = computed(() =>
      distributorReports.value?._items.filter((item) => item.statusMurn === LE.ReportStatus.Draft),
    );

    const scheduledReports = computed(() =>
      distributorReports.value?._items.filter((item) => item.statusMurn === LE.ReportStatus.Scheduled),
    );

    const myDistributorsViewAllTo = computed(() => {
      if (isGetDistributorsSuccess.value && distributors.value?._items?.length) {
        return { name: routeNames.distributor.list };
      }
      return undefined;
    });

    const hasNoDistributors = computed(() => isGetDistributorsSuccess.value && !distributors.value?._items?.length);

    const hasMonthlyDistributors = computed(() =>
      distributors.value?._items?.some((d) => d.reportFrequencyMurn === LE.DistributorReportFrequency.Monthly),
    );

    const hasSemiAnnualDistributors = computed(() =>
      distributors.value?._items?.some((d) => d.reportFrequencyMurn === LE.DistributorReportFrequency.SemiAnnual),
    );

    function getDistributorReportRoute(item: DistributorReport): RouteLocationRaw {
      if (item.statusMurn === LE.ReportStatus.Draft) {
        return { name: routeNames.distributorReport.edit, params: { id: item.id } };
      }
      if (item.statusMurn === LE.ReportStatus.Scheduled && !item.isReportStarted) {
        return {
          name: routeNames.distributorReport.edit,
          params: { id: item.id },
          query: { start: "true" },
        };
      }
      return { name: routeNames.distributorReport.view, params: { id: item.id } };
    }

    function formatDueDate(item: DistributorReport) {
      return item.dueDate ? sysDate.shortDateString(item.dueDate) : "";
    }

    return {
      pastDueReports,
      draftReports,
      scheduledReports,
      distributors,
      userRef,
      appSettingsStore,
      routeNames,
      hasNoDistributors,
      hasMonthlyDistributors,
      hasSemiAnnualDistributors,
      isGetDistributorReportsPending,
      isGetDistributorReportsSuccess,
      isGetDistributorsPending,
      myDistributorsViewAllTo,
      formatDueDate,
    };
  },
});
</script>
